import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { "data-testid": "user-avatar-button" };
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuth } from "@/shared/composables/useAuth";
import { usePreferences } from "@/shared/composables/usePreferences";
import { cleanAuthData } from "@/shared/utils/authCleanup";
import { useLogger } from "@/shared/composables/useLogger";
import { useI18n } from "vue-i18n";
import { keycloak } from "@/plugins/keycloak";
import { Dropdown, Tooltip } from "@eudonet/opal";
import { useAccountInfo } from "@/shared/composables/useAccountInfo";
export default /*@__PURE__*/ _defineComponent({
    __name: 'UserAvatar',
    setup(__props) {
        const logger = useLogger();
        const router = useRouter();
        const { t } = useI18n();
        const { accountInfo, fetchAccountInfo, hasAccountInfo } = useAccountInfo();
        const organizationName = computed(() => accountInfo.value?.name);
        // Use Keycloak authentication
        const { user, userDisplayName, logout: authLogout } = useAuth();
        const { theme, toggleTheme } = usePreferences();
        // Dark theme not production-ready. Expose the toggle only in local dev builds,
        // plus as an escape hatch for users already on dark so they can return to light.
        const showThemeToggle = computed(() => theme.value !== "light" || !import.meta.env.PROD);
        // User info from Keycloak
        const userName = computed(() => {
            if (user.value) {
                return (user.value.name ||
                    user.value.preferred_username ||
                    (user.value.given_name && user.value.family_name
                        ? `${user.value.given_name} ${user.value.family_name}`
                        : user.value.given_name || user.value.family_name) ||
                    user.value.email ||
                    "User");
            }
            return userDisplayName.value || "User";
        });
        const userAvatar = computed(() => {
            const name = userName.value;
            return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=0D8ABC&color=fff&size=48`;
        });
        const resetPassword = (event) => {
            if (event)
                event.preventDefault();
            const organization = localStorage.getItem("organizationCache");
            const redirectUri = window.location.origin + window.location.pathname + "?passwordReset=success";
            logger.debug("Redirecting to Keycloak UPDATE_PASSWORD action...");
            keycloak.login({
                action: "UPDATE_PASSWORD",
                scope: organization ? `organization:${organization}` : "organization",
                redirectUri,
            });
        };
        const logout = async (event) => {
            if (event)
                event.preventDefault();
            try {
                logger.debug("Starting logout process...");
                await authLogout();
            }
            catch (error) {
                logger.debug("Logout failed:", error);
                cleanAuthData();
                await router.push("/session-ended");
            }
        };
        const avatarDropdownItems = computed(() => {
            const items = [];
            if (showThemeToggle.value) {
                items.push({
                    label: theme.value === "light" ? t("theme.light") : t("theme.dark"),
                    hasSwitch: true,
                    switchChecked: theme.value === "dark",
                    hasDividerAfter: true,
                    onClick: () => toggleTheme(),
                    testId: "toggle-theme-button",
                });
            }
            items.push({
                label: t("auth.resetPassword"),
                hasDividerAfter: true,
                onClick: resetPassword,
                testId: "reset-password-button",
            }, {
                label: t("auth.logout"),
                icon: "ri-logout-box-line",
                onClick: logout,
                testId: "logout-button",
            });
            return items;
        });
        onMounted(() => {
            if (!hasAccountInfo.value) {
                fetchAccountInfo();
            }
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(Tooltip), {
                    text: organizationName.value ?? '',
                    disabled: !organizationName.value,
                    position: "bottom"
                }, {
                    default: _withCtx(() => [
                        _createVNode(_unref(Dropdown), {
                            avatar: { name: userName.value, img: userAvatar.value },
                            items: avatarDropdownItems.value,
                            "menu-align": "right",
                            "menu-width": "260px",
                            "data-testid": "user-avatar-dropdown-trigger"
                        }, null, 8 /* PROPS */, ["avatar", "items"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["text", "disabled"])
            ]));
        };
    }
});
